import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {
  FormGroup,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { NgbActiveModal, NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { EMPTY, Observable, Subject } from 'rxjs';
import { catchError, first, startWith, takeUntil } from 'rxjs/operators';
import { ObservableFunction } from 'src/app/common-types/common-types.consts';
import { tradingDayValidator } from 'src/app/form-config/validators/cross-field-validators/trading-day.validator';
import {
  ANALYTICS_SOURCE,
  AnalyticsSourcesKeys,
} from 'src/app/product-capture/model/consts/analytics-source.const';
import { AnalyticsRequestObject } from 'src/app/product-capture/model/interfaces/analytics-request-object.interface';
import { AnalyticsService } from 'src/app/product-capture/services/analytics.service';
import { GetErrorMessageFromResponse } from 'src/app/product-capture/utils/get-error-message-from-response';
import { NumberUtils } from 'src/app/product-capture/utils/number.utils';
import { DcAlertService } from 'src/app/ui-kit/dc-alert/dc-alert.service';
import { DateUtils } from 'src/app/utils/date.utils';
import { AnalyticsReportsTypes } from '../analytics.conts';
import { CloudRiskErrorDetail } from './cloud-risk.consts';

@Component({
  selector: 'app-analytics-request-modal',
  templateUrl: './analytics-request-modal.component.html',
  styleUrls: ['./analytics-request-modal.component.scss'],
})
export class AnalyticsRequestModalComponent implements OnInit, OnDestroy {
  form: UntypedFormGroup;
  source = ANALYTICS_SOURCE;
  destroy$ = new Subject();
  loading$ = new Subject<boolean>();
  props: { productId; fvcAsOfDefault; productType; reportType };
  productId: number;
  fvcAsOfDefault: NgbDateStruct;
  productType: string | null;

  BENCHMARK_TYPES = [
    {
      id: true,
      displayValue: 'Hurdle Rate',
    },
    {
      id: false,
      displayValue: 'Spread',
    },
  ];

  @ViewChild('locationTempRef', { static: true })
  locationTempRef: TemplateRef<any>;

  @ViewChild('typeTempRef', { static: true })
  typeTempRef: TemplateRef<any>;

  saveCallback: ObservableFunction<AnalyticsRequestObject>;

  cloudRiskError: CloudRiskErrorDetail;

  cloudRiskColumns = [];
  messages = {
    emptyMessage: '',
  };

  defaultHurdleOrSpread: { spread?: number; hurdleRate?: number } = {};

  defaultHurdleOrSpreadLoading = false;

  constructor(
    public modal: NgbActiveModal,
    private fb: UntypedFormBuilder,
    private analyticsService: AnalyticsService,
    private alertService: DcAlertService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    const { productId, fvcAsOfDefault, productType, reportType } = this.props;
    this.productId = productId;
    this.productType = productType;
    this.fvcAsOfDefault = fvcAsOfDefault;

    this.cloudRiskColumns = [
      {
        name: 'Location',
        prop: 'loc',
        resizeable: false,
        draggable: false,
        flexGrow: 1,
        minWidth: 250,
        maxWidth: 350,
        cellTemplate: this.locationTempRef,
      },
      {
        name: 'Type',
        prop: 'type',
        resizeable: false,
        draggable: false,
        maxWidth: 120,
        minWidth: 120,
        cellTemplate: this.typeTempRef,
      },
      {
        name: 'Message',
        prop: 'msg',
        resizeable: false,
        draggable: false,
        flexGrow: 1,
        maxWidth: 270,
      },
    ];

    this.form =
      this.productType === 'CLN'
        ? this.createForm('cloudRiskStress')
        : this.createForm('fvc');

    this.requestTypeValue.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((val) => {
        const asofDate = this.asof.value ?? this.fvcAsOfDefault;
        this.asof.setValue(asofDate);
        this.asof.enable();
        this.asof.clearValidators();

        if (val === 'fvc') {
          this.asof.setValidators([Validators.required, tradingDayValidator()]);
          this.makeFieldsInvisible('fairValue', 'spread');
          this.makeFieldVisibleAndRequired('hurdleRate');
          this.benchmarkMode?.enable();
          this.benchmarkMode?.setValue(true);
        }
        if (val === 'cloudRisk' || val === 'fvcPRIIPs') {
          this.asof.setValidators([tradingDayValidator()]);
          this.makeFieldVisibleAndRequired('fairValue', '');
          this.makeFieldsInvisible('spread', 'hurdleRate');
          this.benchmarkMode?.disable();
        }
        if (val === 'cloudRiskStress') {
          this.asof.setValidators([tradingDayValidator()]);
          this.makeFieldsInvisible('fairValue', 'hurdleRate');
          this.makeFieldVisibleAndRequired('spread');
          this.benchmarkMode?.enable();
          this.benchmarkMode?.setValue(false);
        }
        this.form.updateValueAndValidity();
        this.cdr.detectChanges();
      });

    this.defaultRequestTypeIfAvailable(reportType);

    this.asof.valueChanges
      .pipe(takeUntil(this.destroy$), startWith(this.asof.value))
      .subscribe(() => this.loadDefaultHurdleOrSpread());

    this.adjustFieldsBasedOnBenchmarkMode(this.benchmarkMode?.value);
    this.benchmarkMode?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((val) => this.adjustFieldsBasedOnBenchmarkMode(val));
  }

  defaultRequestTypeIfAvailable(reportType: AnalyticsReportsTypes): void {
    if (!reportType) {
      return;
    }

    let reportTypeValue = null;
    if (this.productType === 'CLN') {
      reportTypeValue =
        reportType === AnalyticsReportsTypes.STRESS
          ? AnalyticsSourcesKeys.CLOUD_RISK_STRESS
          : AnalyticsSourcesKeys.CLOUD_RISK;
    } else {
      reportTypeValue =
        reportType === AnalyticsReportsTypes.STRESS
          ? AnalyticsSourcesKeys.FVC
          : AnalyticsSourcesKeys.FVC_PRIIPs;
    }

    reportTypeValue = this.source.find((obj) => obj.id === reportTypeValue).id;

    this.requestTypeValue.setValue(reportTypeValue);
  }

  get requestTypeValue() {
    return this.form.get('requestType');
  }

  get fairValue() {
    return this.form.get('fairValue');
  }

  get asof() {
    return this.form.get('asof');
  }

  get spread() {
    return this.form.get('spread');
  }

  get hurdleRate() {
    return this.form.get('hurdleRate');
  }

  get isHurdleMode() {
    return this.form.get('isHurdleMode');
  }

  get benchmarkMode() {
    return this.form.get('benchmarkMode');
  }

  cancel(): void {
    this.loading$.next(false);
    this.modal.close('cancel');
  }

  save(): void {
    this.loading$.next(true);

    const requestType = this.requestTypeValue.value;
    const asof = DateUtils.getStringFromDateObject(this.asof.value);
    const spread = NumberUtils.stringToNumber(this.spread?.value);
    const hurdleRate = NumberUtils.stringToNumber(this.hurdleRate?.value);
    const fairValue = NumberUtils.stringToNumber(this.fairValue.value);

    const onErrorHandler =
      this.requestTypeValue.value === 'fvc'
        ? (e: HttpErrorResponse) => {
            console.error(e);
            this.loading$.next(false);
            this.modal.close({
              result: 'error',
              tracerMessage: e.error.tracer,
            });
          }
        : (e: HttpErrorResponse) => {
            this.cloudRiskErrorHandler(e);
          };

    const payload = {
      requestType,
      ...(Number.isFinite(spread) && { spread }),
      ...(Number.isFinite(hurdleRate) && { hurdleRate }),
      ...(Number.isFinite(fairValue) && { fairValue }),
      ...(asof && { asof }),
    };

    this.analyticsService
      .postAnalytics(this.productId, payload)
      .subscribe(() => {
        this.loading$.next(false);
        this.modal.close({ result: 'success' });
      }, onErrorHandler);
  }

  cloudRiskErrorHandler(e): Observable<never> {
    const { error } = e;

    if (!error?.errorData?.detail || this.cloudRiskError) {
      const { errors, tracer, status } = error;
      this.loading$.next(false);
      this.modal.close({
        result: status ?? 'error',
        tracerMessage: tracer,
        errorMessage: errors,
      });
      return EMPTY;
    }

    if (typeof error?.errorData?.detail === 'string') {
      this.loading$.next(false);
      this.modal.close({
        result: 'error',
        errorMessage: error.errorData.detail,
      });
      return EMPTY;
    }

    if (
      typeof error?.errorData?.detail !== 'string' &&
      error.errorData.detail.length
    ) {
      const { classList } = document.querySelector('.modal-md');
      classList.remove('modal-md');
      classList.add('modal-lg');
      this.cloudRiskError = error.errorData.detail;
      this.loading$.next(false);
      return EMPTY;
    }

    this.loading$.next(false);
    this.modal.close({ result: 'error' });
    return EMPTY;
  }

  adjustFieldsBasedOnBenchmarkMode(isHurdleMode: boolean): void {
    if (
      this.requestTypeValue.value !== 'cloudRiskStress' &&
      this.requestTypeValue.value !== 'fvc'
    ) {
      return;
    }

    if (isHurdleMode === true) {
      this.makeFieldsInvisible('spread');
      this.makeFieldVisibleAndRequired('hurdleRate');
      this.hurdleRate.setValue(this.defaultHurdleOrSpread.hurdleRate);
    } else if (isHurdleMode === false) {
      // Explicit check for false to avoid null/undefined
      this.makeFieldVisibleAndRequired('spread');
      this.makeFieldsInvisible('hurdleRate');
      this.spread.setValue(this.defaultHurdleOrSpread.spread);
    }

    this.form.updateValueAndValidity();
  }

  private makeFieldVisibleAndRequired(
    field: string,
    defaultValue?: string
  ): void {
    const formField = this.form.get(field);
    formField?.enable();
    formField?.setValue(defaultValue || null);
    formField?.clearValidators();
    formField?.setValidators([Validators.required]);
  }

  private makeFieldsInvisible(...fields: string[]): void {
    fields.forEach((field) => {
      const formField = this.form.get(field);
      formField?.disable();
      formField?.setValue(null);
      formField?.clearValidators();
    });
  }

  private createForm(source: string): FormGroup {
    return this.fb.group({
      requestType: [source],
      asof: [
        { value: this.fvcAsOfDefault, disabled: false },
        [Validators.required, tradingDayValidator()],
      ],
      fairValue: [{ value: null, disabled: true }],
      spread: [{ value: null, disabled: false }, Validators.required],
      hurdleRate: [{ value: null, disabled: false }, Validators.required],
      benchmarkMode: [
        { value: source === 'fvc' ? true : false, disabled: false },
      ],
    });
  }

  private loadDefaultHurdleOrSpread(): void {
    this.defaultHurdleOrSpreadLoading = true;
    if (
      this.requestTypeValue.value === AnalyticsSourcesKeys.CLOUD_RISK ||
      this.requestTypeValue.value === AnalyticsSourcesKeys.FVC_PRIIPs
    ) {
      this.defaultHurdleOrSpreadLoading = false;
      return;
    }

    const asOfDateAsString = DateUtils.getStringFromDateObject(this.asof.value);

    this.analyticsService
      .getDefaultHurdleOrSpreadValue(this.productId, asOfDateAsString)
      .pipe(
        first(),
        catchError((error: HttpErrorResponse) => {
          this.alertService.showAlert({
            message: `${GetErrorMessageFromResponse(error)}`,
            type: 'danger',
          });
          this.defaultHurdleOrSpread = { spread: null, hurdleRate: null };
          this.hurdleRate.setValue(null);
          this.spread.setValue(null);
          this.defaultHurdleOrSpreadLoading = false;

          return EMPTY;
        })
      )
      .subscribe(({ spread, hurdleRate }) => {
        this.defaultHurdleOrSpread = { spread, hurdleRate };
        this.benchmarkMode.setValue(!!hurdleRate);
        this.defaultHurdleOrSpreadLoading = false;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.unsubscribe();
  }
}
